import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterModule } from '@angular/router';

import { BehaviorSubject, filter, firstValueFrom, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { BreadcrumbSegment, BreadcrumbService } from '@/theme/service/breadcrumb.service';

export interface BreadcrumbRouteData {
    breadcrumb?: string;
    breadcrumbKey?: string;
}

interface Breadcrumb {
    label: string;
    url?: string;
}

@Component({
    selector: '[app-breadcrumb]',
    standalone: true,
    imports: [CommonModule, RouterModule],
    template: `<nav class="layout-breadcrumb">
        <ol>
            <ng-template ngFor let-item let-last="last" [ngForOf]="breadcrumbs$ | async">
                <li class="text-surface-950 dark:text-surface-0 title-h7 text-xl">{{ item.label }}</li>
                <li *ngIf="!last" class="layout-breadcrumb-chevron">/</li>
            </ng-template>
        </ol>
    </nav> `
})
export class AppBreadcrumb implements OnInit, OnDestroy {
    private readonly _breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);
    readonly breadcrumbs$ = this._breadcrumbs$.asObservable();

    private subscriptions = new Subscription();
    componentBreadcrumbs: BreadcrumbSegment[] = [];

    constructor(
        private router: Router,
        private translate: TranslateService,
        private breadcrumbService: BreadcrumbService
    ) {

    }

    ngOnInit() {
        this.subscriptions.add(this.breadcrumbService.breadcrumbs$.subscribe(async (segments) => {
            this.componentBreadcrumbs = segments;
            await this.updateBreadcrumb();
        }));
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(async (event) => {
            await this.updateBreadcrumb();
        });
        // TODO fix flapping page title on initial page load:
        this.translate.onLangChange.subscribe(async () => {
            await this.updateBreadcrumb();
        });
        //this.updateBreadcrumb();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    async updateBreadcrumb() {
        const root = this.router.routerState.snapshot.root;
        const breadcrumbs: Breadcrumb[] = [];
        await this.addBreadcrumb(root, [], breadcrumbs);
        this._breadcrumbs$.next(breadcrumbs);
    }

    private async addBreadcrumb(route: ActivatedRouteSnapshot, parentUrl: string[], breadcrumbs: Breadcrumb[]): Promise<void> {
        const routeUrl = parentUrl.concat(route.url.map((url) => url.path));
        const breadcrumb: string | undefined = await this.getBreadCrumbText(this.getData(route));
        const parentBreadcrumb = route.parent && route.parent.data ? await this.getBreadCrumbText(this.getData(route.parent)) : null;

        if (breadcrumb && breadcrumb !== parentBreadcrumb) {
            breadcrumbs.push({
                label: breadcrumb,
                url: '/' + routeUrl.join('/')
            });
        }

        if (route.firstChild) {
            await this.addBreadcrumb(route.firstChild, routeUrl, breadcrumbs);
        }
    }

    private getData(route: ActivatedRouteSnapshot): BreadcrumbRouteData {
        const componentBreadcrumb = this.componentBreadcrumbs.find(
            (segment) => segment.component === route.component);
        if (componentBreadcrumb) {
            return componentBreadcrumb.data;
        }
        return route.data;
    }

    private async getBreadCrumbText(data: BreadcrumbRouteData): Promise<string | undefined> {
        if (data.breadcrumbKey) {
            return await firstValueFrom(this.translate.get(data.breadcrumbKey));
        }
        else if (data.breadcrumb) {
            return data.breadcrumb;
        }
        return undefined;
    }
}
